import { PerspectiveCamera } from 'three/src/cameras/PerspectiveCamera';
import { LinearToneMapping, sRGBEncoding } from 'three/src/constants';
import { DirectionalLight } from 'three/src/lights/DirectionalLight';
import { WebGLRenderer } from 'three/src/renderers/WebGLRenderer';
import { AmbientLight } from 'three/src/lights/AmbientLight';

import type { Group } from 'three/src/objects/Group';
import { Vector3 } from 'three/src/math/Vector3';
import { Scene } from 'three/src/scenes/Scene';
import { Color } from 'three/src/math/Color';

export default class AvatarHeadshot {
  private renderer!: WebGLRenderer;
  private camera!: PerspectiveCamera;
  private readonly scene = new Scene();

  public constructor(private readonly canvas: HTMLCanvasElement) {
    this.createRenderer(innerWidth, innerWidth);
    this.createCamera(innerWidth / innerWidth);
    this.createLights();
  }

  private createRenderer(width: number, height: number): void {
    this.renderer = new WebGLRenderer({
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true,
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });

    this.renderer.setPixelRatio(devicePixelRatio || 1.0);
    this.renderer.setClearColor(Color.NAMES.black, 0.0);

    this.renderer.toneMapping = LinearToneMapping;
    this.renderer.outputEncoding = sRGBEncoding;
    this.renderer.setSize(width, height);
  }

  private createCamera(ratio: number): void {
    this.camera = new PerspectiveCamera(50.0, ratio, 0.1, 100.0);
    this.camera.position.set(0.0, 27.5, 47.5);
    this.scene.add(this.camera);
  }

  private createLights(): void {
    const directional = new DirectionalLight(Color.NAMES.white, 1.0);
    const ambient = new AmbientLight(Color.NAMES.white, 0.25);

    directional.position.set(0.0, 22.5, 2.5);
    directional.lookAt(0.0, 21.0, 0.0);

    this.camera.add(directional);
    this.scene.add(ambient);
  }

  public capture(avatar: Group, size = 1.0): string {
    const zoom = new Vector3(
      0.0,
      size === 1 ? 23.53 : size === 2 ? 28.75 : 34.0,
      size === 1 ? 13.7 : size === 2 ? 16.85 : 20.0
    );

    this.scene.add(avatar);
    this.camera.position.set(0.0, 27.5, 47.5);

    this.camera.position.copy(
      new Vector3()
        .subVectors(this.camera.position, zoom)
        .setLength(1.0)
        .add(zoom)
    );

    this.renderer.render(this.scene, this.camera);
    this.scene.remove(avatar);

    return this.canvas.toDataURL('image/png', 1.0);
  }

  public dispose(): void {
    this.renderer.domElement.remove();
    this.renderer.dispose();
    this.scene.clear();
  }
}
